/**
 * useStarterTemplate — seed a brand-new site from its chosen starter template
 * BEFORE the canvas mounts, so `readInitialSitemap` finds the template's pages
 * instead of falling back to the default "Home + hero".
 *
 * Returns `ready`: false while a snapshot is being applied. The caller keeps
 * the canvas unmounted until then (one extra frame on a new site, nothing on
 * a reopened one). Gated exactly like the default seed: only when the doc is
 * loaded, this tab may seed (`canSeed`), and the doc is still empty.
 */
import { useEffect, useState } from "react";
import type * as Y from "yjs";

import type { EditorDoc } from "./useEditorDoc.js";
import { hasStarterSnapshot, loadStarterTemplate } from "./starterTemplates.js";

export function useStarterTemplate(
  editorDoc: Pick<EditorDoc, "ydoc" | "loaded" | "canSeed">,
  templateId: string | undefined,
): boolean {
  const { ydoc, loaded, canSeed } = editorDoc;
  const wants =
    loaded &&
    canSeed &&
    templateId !== undefined &&
    hasStarterSnapshot(templateId) &&
    ydoc.getMap("nodes").size === 0;
  const [applied, setApplied] = useState<Y.Doc | null>(null);

  useEffect(() => {
    if (!wants || applied === ydoc) return;
    let disposed = false;
    void loadStarterTemplate(ydoc, templateId).finally(() => {
      if (!disposed) setApplied(ydoc);
    });
    return () => {
      disposed = true;
    };
  }, [wants, applied, ydoc, templateId]);

  return !wants || applied === ydoc;
}
